// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xHeaderUpdate from "@plumberssupply/App/Handlers/XNG/Header";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import FulfillmentMethodChangeModal from "@plumberssupply/App/Widgets/FulfillmentMethodAndCustomerLinks/FulfillmentMethodChangeModal";
import { EMPTY_VALUE } from "@plumberssupply/XDL/constants";
import IconEdit from "@plumberssupply/XDL/Icons/IconEdit";
import { ellipsis, getSystemColor } from "@plumberssupply/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    const session = getSession(state);
    const currentShipToState = getShipToState(state, session.shipToId);
    const currentBillToState = getBillToState(state, session.billToId);

    const customerNumberValue = currentBillToState?.value ? currentBillToState.value.customerNumber : EMPTY_VALUE;
    const customerNameValue = currentBillToState?.value ? currentBillToState.value.customerName.toLowerCase() : EMPTY_VALUE;

    const fulfillmentMethodValue =
        session.fulfillmentMethod === FulfillmentMethod.PickUp
            ? session.pickUpWarehouse?.description?.toLowerCase() || EMPTY_VALUE
            : currentShipToState?.value?.city?.toLowerCase() || EMPTY_VALUE;

    return {
        customerNumberValue,
        customerNameValue,
        fulfillmentMethod: session.fulfillmentMethod,
        fulfillmentMethodValue,
        changeCustomerPageUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
        userSetupPageUrl: getPageLinkByPageType(state, "UserSetupPage")?.url,
        isTabletScreen: state.xCommon.isTabletScreen,
        currentLocation: getLocation(state),
    };
};

const mapDispatchToProps = {
    xHeaderUpdate,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface FulfillmentMethodAndCustomerLinksStyles {
    container?: InjectableCss;
    link?: LinkPresentationProps;
    label?: TypographyProps;
    text?: TypographyProps;
}

const fulfillmentMethodAndCustomerLinksStyles: FulfillmentMethodAndCustomerLinksStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            column-gap: 55px;
        `,
    },
    link: {
        color: "common.white",
        typographyProps: {
            size: 12,
            weight: "normal",
            css: css`
                letter-spacing: 0;
                line-height: 22px;

                display: flex;
                align-items: center;
                gap: 5px;
            `,
        },
        icon: {
            iconProps: {
                src: IconEdit,
                color: getSystemColor("common.white", 0.66),
                size: 12,
            },
            position: "right",
        },
    },
    label: {
        weight: "bold",
        css: css`
            text-transform: capitalize;
            white-space: nowrap;
        `,
    },
    text: {
        weight: "normal",
        css: css`
            max-width: 250px;
            text-transform: capitalize;
            ${ellipsis(1)}
        `,
    },
};

const styles = fulfillmentMethodAndCustomerLinksStyles;

const FulfillmentMethodAndCustomerLinks: FC<Props> = ({
    fulfillmentMethodValue,
    customerNumberValue,
    customerNameValue,
    fulfillmentMethod,
    isTabletScreen,
    changeCustomerPageUrl,
    userSetupPageUrl,
    xHeaderUpdate,
    currentLocation,
}) => {
    const history = useHistory();
    const handleChangeCustomerPageRedirect = () => {
        const returnUrl = currentLocation.pathname + currentLocation.search;
        if (
            returnUrl === "/" ||
            currentLocation.pathname === changeCustomerPageUrl ||
            (userSetupPageUrl && returnUrl.includes(userSetupPageUrl))
        ) {
            history.push(`${changeCustomerPageUrl}`);
        } else {
            history.push(`${changeCustomerPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
        }
    };

    return (
        <StyledWrapper {...styles.container}>
            <Link
                {...styles.link}
                type="button"
                onClick={handleChangeCustomerPageRedirect}
                data-test-selector="header_CustomerNumber"
            >
                <Typography {...styles.label}>{translate("Customer")}:</Typography>
                <Typography {...styles.text}>#{customerNumberValue} - {customerNameValue}</Typography>
            </Link>
            {!isTabletScreen && (
                <Link
                    {...styles.link}
                    type="button"
                    onClick={() => xHeaderUpdate({ key: "fulfillmentMethodModalOpen", value: true })}
                    data-test-selector="header_fulfillmentMethod"
                >
                    {fulfillmentMethod === FulfillmentMethod.PickUp ? `${translate("Pick Up At")}: ` : `${translate("Delivering To")}: `}
                    <Typography {...styles.text}>{fulfillmentMethodValue}</Typography>
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FulfillmentMethodAndCustomerLinks),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Fulfillment Method & Customer Links",
        fieldDefinitions: [],
        allowedContexts: ["Header", "CompactHeader"],
    },
};

export default widgetModule;
